import React from 'react';
import { Tooltip } from '@servicetitan/anvil2';
import styles from './OverflowTooltip.module.css';
import classNames from 'classnames';

interface OverflowTooltipProps {
  children: React.ReactNode & React.ReactElement;
  /** Content to display in the tooltip. Defaults to children if not provided. */
  content?: React.ReactNode;
  className?: string;
}

/**
 * A component that detects if the content is overflowing and displays a tooltip if it is.
 * Renders the children as is, but detects if the content is overflowing and displays a tooltip if it is.
 *
 * @param props - The properties for the overflow tooltip.
 * @returns A React component that detects if the content is overflowing and displays a tooltip if it is.
 */
export const OverflowTooltip = (props: OverflowTooltipProps) => {
  const { children, content, className } = props;

  const ref = React.useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  const checkOverflow = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const hasOverflow =
      el.scrollWidth > el.clientWidth ||
      Array.from(el.querySelectorAll('*')).some(
        (child) => child.scrollWidth > child.clientWidth,
      );
    setIsOverflowing(hasOverflow);
  }, []);

  // Re-check on every render to catch content changes
  React.useLayoutEffect(checkOverflow);

  // ResizeObserver handles container resizes that don't trigger a re-render
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkOverflow]);

  return (
    <span ref={ref} className={classNames(styles.overflowDetector, className)}>
      <Tooltip>
        <Tooltip.Trigger className={styles.tooltipTrigger}>
          {children}
        </Tooltip.Trigger>
        {isOverflowing && (
          <Tooltip.Content>{content ?? children}</Tooltip.Content>
        )}
      </Tooltip>
    </span>
  );
};
